/**
 * `annotationWire.ts` — the JsonUtility WIRE shape of annotation live events
 * (`create_annotation` / `edit_annotation` tc 2, `delete_annotation` tc 10).
 *
 * This is a DIFFERENT shape from the Dynamo row mappers in `annotationDto.ts`:
 *
 * | field            | Dynamo (`annotationDto`)     | wire (here)                  |
 * | ---------------- | ---------------------------- | ---------------------------- |
 * | text             | `AnnotationText` (S)         | `annotationText`             |
 * | positions        | `M` maps `{x,y,z}`           | `[{name,val}]` pair arrays   |
 * | created/modified | ISO strings                  | ms longs (`timems`/`modTS`)  |
 * | reply text       | `Message` + `CreatedTS`      | `Message` + `createdTS` (ms) |
 * | resolved         | `IsResolved` int             | `IsResolved` int (same)      |
 *
 * Mixed casing is verbatim Unity: `[SerializeField]`-private fields serialize
 * camelCase, public fields keep their C# capitalization (`ID`, `SceneID`,
 * `CreatorId`, `IsResolved`, `Fov`).
 *
 * Vector pair-arrays ALWAYS emit every axis: Unity's `OnAfterDeserialize`
 * builds dictionaries from them with no null guard, and its own creators seed
 * dummy zeroes for exactly that reason (`AnnotationsLayerModel.cs:949-952`).
 */

import type {
  AnnotationData,
  AnnotationQuat,
  AnnotationReplyData,
  AnnotationVec3,
} from "./annotationData";

/** Unity `AnnotationUpdateEventFlags` (`AnnotationEventData.cs`). */
export const ANNOTATION_UPDATE_FLAGS = {
  /** Whole-annotation edit. `a.replies` is EMPTY — receivers keep theirs. */
  Annotation: 0,
  AddReply: 1,
  DeleteReply: 2,
  EditReply: 3,
} as const;

/** Unity `AnnotationType` ints (`AnnotationData.annotationType`). */
const WIRE_TYPE_TEXT = 0;
const WIRE_TYPE_SKETCH = 1;

interface NameFloatPair {
  readonly name: string;
  readonly val: number;
}

/** `AnnotationEventData.update` — the create/edit payload wrapper. */
export interface AnnotationUpdateWire {
  readonly flags: number;
  readonly rId: string;
  readonly a: Record<string, unknown>;
}

function pairs(
  axes: ReadonlyArray<string>,
  values: ReadonlyArray<number> | undefined,
): ReadonlyArray<NameFloatPair> {
  return axes.map((name, i) => ({ name, val: values?.[i] ?? 0 }));
}

function vec3FromPairs(value: unknown): AnnotationVec3 | undefined {
  const map = pairMap(value);
  if (!map) return undefined;
  const vec: AnnotationVec3 = [map.x ?? 0, map.y ?? 0, map.z ?? 0];
  // Unity seeds DUMMY ZERO vectors on create because its `OnAfterDeserialize`
  // throws on a missing key (`AnnotationsLayerModel`: "dummy values needed
  // because serialization ... throws"), and this sender always emits every
  // axis for the same reason. So all-zero means "no placement pose", not
  // "placed at the world origin" — treating it literally renders a phantom
  // marker at origin for every detached, list-only comment.
  if (vec[0] === 0 && vec[1] === 0 && vec[2] === 0) return undefined;
  return vec;
}

function quatFromPairs(value: unknown): AnnotationQuat | undefined {
  const map = pairMap(value);
  if (!map) return undefined;
  return [map.x ?? 0, map.y ?? 0, map.z ?? 0, map.w ?? 1];
}

function pairMap(value: unknown): Record<string, number> | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const out: Record<string, number> = {};
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const name = typeof record.name === "string" ? record.name : null;
    const val = Number(record.val);
    if (name && Number.isFinite(val)) out[name] = val;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function msFromIso(iso: string | undefined): number {
  if (!iso) return 0;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isoFromMs(value: unknown, fallback: string): string {
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return fallback;
  return new Date(ms).toISOString();
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function replyToWire(reply: AnnotationReplyData): Record<string, unknown> {
  return {
    ID: reply.id,
    Message: reply.message,
    CreatorId: reply.creator.id,
    CreatorNaturalAccountId: reply.creator.naturalAccountId ?? "",
    CreatorInitials: reply.creator.initials,
    CreatorName: reply.creator.name,
    createdTS: msFromIso(reply.createdAt),
    modifiedTS: msFromIso(reply.modifiedAt),
    // Unity `AttachmentType.Annotation` (measurements use 1).
    parentType: 0,
  };
}

export function replyFromWire(
  value: unknown,
  parentId: string,
): AnnotationReplyData | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = str(record.ID) || str(record.id);
  if (!id) return null;
  const nowIso = isoFromMs(record.createdTS, new Date(0).toISOString());
  return {
    id,
    parentId,
    message: str(record.Message),
    creator: {
      id: str(record.CreatorId),
      name: str(record.CreatorName),
      initials: str(record.CreatorInitials),
      naturalAccountId: str(record.CreatorNaturalAccountId) || undefined,
    },
    createdAt: nowIso,
    modifiedAt: isoFromMs(record.modifiedTS, nowIso),
  };
}

/**
 * `AnnotationData` → wire. `includeReplies` mirrors Unity's
 * `AnnotationUpdateData.CopyTo(copyReplies: false)`: a flags-0 whole-annotation
 * edit sends NO replies (receivers keep their own list); the reply flags send
 * exactly the one affected reply.
 */
export function annotationToWire(
  annotation: AnnotationData,
  replies: ReadonlyArray<AnnotationReplyData> = [],
): Record<string, unknown> {
  return {
    annotationType: annotation.type === "sketch" ? WIRE_TYPE_SKETCH : WIRE_TYPE_TEXT,
    parentEntitySnapshotIds: [],
    forces: [],
    posVecs: [],
    visualizationType: 0,
    sketchUploaded: annotation.sketchUploaded === true,
    annotationText: annotation.text,
    // Web deviation (documented in `annotationData.ts`): world-space
    // placement with an EMPTY parent snapshot id.
    parentEntitySnapshotId: "",
    actionDataId: "",
    isSceneVisible: true,
    attachmentPosition: pairs(["x", "y", "z"], annotation.attachmentPosition),
    placementPosition: pairs(["x", "y", "z"], annotation.placementPosition),
    placementRotation: pairs(["x", "y", "z", "w"], annotation.placementRotation),
    CreatorId: annotation.creator.id,
    CreatorNaturalAccountId: annotation.creator.naturalAccountId ?? "",
    CreatorInitials: annotation.creator.initials,
    CreatorName: annotation.creator.name,
    SceneID: annotation.sceneId,
    ID: annotation.id,
    IsResolved: annotation.isResolved ? 1 : 0,
    timems: msFromIso(annotation.createdAt),
    modTS: msFromIso(annotation.modifiedAt),
    isExplosionAnnotation: false,
    IsTextEdited: false,
    Fov: annotation.fov ?? 60,
    OrthographicSize: annotation.orthographicSize ?? 0,
    replies: replies.map(replyToWire),
  };
}

/** The `annotationType` ints the web renders (text + sketch). */
export function isSupportedWireAnnotationType(value: unknown): boolean {
  const type = num(value);
  return type === WIRE_TYPE_TEXT || type === WIRE_TYPE_SKETCH;
}

/**
 * Wire → `AnnotationData`. Returns null for ids/types the web doesn't model
 * (collision batches `2`, action buttons `3` — the actions model is
 * deprioritized). Replies are taken from the payload only when present; a
 * flags-0 edit carries none, and the caller keeps its local list.
 */
export function annotationFromWire(value: unknown): AnnotationData | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (!isSupportedWireAnnotationType(record.annotationType)) return null;
  const id = str(record.ID);
  if (!id) return null;

  const createdAt = isoFromMs(record.timems, new Date().toISOString());
  const rawReplies = Array.isArray(record.replies) ? record.replies : [];
  const replies = rawReplies
    .map((reply) => replyFromWire(reply, id))
    .filter((reply): reply is AnnotationReplyData => reply !== null);

  return {
    id,
    sceneId: str(record.SceneID),
    type: num(record.annotationType) === WIRE_TYPE_SKETCH ? "sketch" : "text",
    text: str(record.annotationText),
    creator: {
      id: str(record.CreatorId),
      name: str(record.CreatorName),
      initials: str(record.CreatorInitials),
      naturalAccountId: str(record.CreatorNaturalAccountId) || undefined,
    },
    createdAt,
    modifiedAt: isoFromMs(record.modTS, createdAt),
    isResolved: num(record.IsResolved) !== 0,
    // Web rows are world-space with an empty parent; "detached" (list-only)
    // means no placement pose at all — and per `vec3FromPairs`, an all-zero
    // pose IS "no pose" (Unity's dummy seed), so derive both from one test so
    // this matches `annotationDto.mapAnnotationDtoToData`.
    isDetached: vec3FromPairs(record.placementPosition) === undefined,
    parentSnapshotId: str(record.parentEntitySnapshotId) || undefined,
    replies,
    attachmentPosition: vec3FromPairs(record.attachmentPosition),
    placementPosition: vec3FromPairs(record.placementPosition),
    placementRotation: quatFromPairs(record.placementRotation),
    sketchUploaded: record.sketchUploaded === true,
    fov: num(record.Fov) || undefined,
    orthographicSize: num(record.OrthographicSize) || undefined,
  };
}
